import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchBookingDetails, fetchMultipleBookingDetails, rebookCustomerDoor } from '../../action/bookingsCommands';
import { Booking, FullMappedDepot, FullMappedDoor } from '../../api/types';
import { tenantSelector } from '../../selector/authenticationSelector';
import { makeBookingMultipleSelector, makeBookingSelector } from '../../selector/bookingsSelector';
import { store } from '../../store/store';
import DoorListItem from './DoorListItem';
import RebookCustomerDoorDialog from './RebookCustomerDoorDialog';

interface DoorListProps {
    depot: FullMappedDepot;
    doorMapping: FullMappedDoor[];
    searchQuery: string;
    onOpenClick: (doorNumber: number) => void;
    onLockOrUnlockClick: (action: 'block' | 'unblock', doorNumber: number) => void;
    reloadDoorStatus: () => void;
    // bookingId?: string;
}

export interface DoorMapping {
    door: number;
    /** Null at a station with no lock controller: there is no lock to report. */
    doorState: string | null;
    assetState: 'available' | 'blocked' | 'inUse' | 'soonAvailable';
    assetId: string;
    orderId?: string;
}

const useBookings = (doorMapping: any) => {
    const selectors = useMemo(() => {
        return doorMapping.map((item: any) => {
            const bookingNumber = item.bookingNumber;
            return bookingNumber ? makeBookingSelector(bookingNumber) : null;
        });
    }, [doorMapping]);
    const bookings = useSelector(state => selectors.map((selector: any) => {
        return selector ? selector(state) : null;
    }));
    return bookings;
};   

const DoorList = (props: DoorListProps) => {
    const dispatch = useDispatch();
    const [rebookDoorWarningOpen, setRebookDoorWarningOpen] = useState<boolean>(false);
    const [rebookConfirmed, setRebookConfirmed] = useState<boolean>(false); // State to track rebook confirmation
    const [selectedDoorToRebook, setSelectedDoorToRebook] = useState<DoorMapping | null>(null);
    const [doorIndex, setDoorIndex] = useState<number | null>(null);

    // eslint-disable-next-line no-console
    console.log('permittedDoors', props.depot.doorMapping);

    const prevDoorMappingRef = useRef(props.depot.doorMapping);
    const bookingIds = props.depot.doorMapping.map((mapping) => mapping.orderId ?? '');
    const tenant = tenantSelector(store.getState());
    const bookingsList = useSelector(makeBookingMultipleSelector(bookingIds, 'id', tenant));

    useEffect(() => {
        const prevDoorMapping = prevDoorMappingRef.current;
        if (
            prevDoorMapping !== props.depot.doorMapping &&
            props.depot.doorMapping?.length &&
            JSON.stringify(prevDoorMapping) !== JSON.stringify(props.depot.doorMapping) &&
            (tenant === 'vonovia' || tenant === 'segmenta')
        ) {
            props.depot.doorMapping.forEach(item => {
                const bookingNumber = item.bookingNumber;
                if (bookingNumber) {
                    dispatch(fetchBookingDetails({ bookingId: bookingNumber }));
                }
            });
            props.reloadDoorStatus();
        }
        prevDoorMappingRef.current = props.depot.doorMapping;
    }, [props.depot.doorMapping, dispatch, rebookConfirmed, props]);
    const bookings = useBookings(props.depot.doorMapping);

    useEffect(() => {
        if (props.depot.doorMapping?.length > 0 && tenant !== 'vonovia' && tenant !== 'segmenta') {
            const bookingIds = props.depot.doorMapping.map((mapping) => mapping.orderId ?? '');
            if (bookingIds.length > 0) {
                dispatch(fetchMultipleBookingDetails({
                    bookingIds: bookingIds.filter((id) => id !== '').join(','),
                }));
            }
        }
    }, [props.depot.doorMapping?.length > 0]);

    const handleRebookDoor = (doorMapping: DoorMapping) => {
        const booking = bookings?.find(
            (item: Booking | undefined) => item?.id === doorMapping.orderId);
        if (booking) {
            dispatch(rebookCustomerDoor({
                bookingId: booking.id,
                depotId: props.depot.id,
                orderItemId: booking.items[0].id,
                doorNumber: doorMapping.door,
            }));
            setRebookDoorWarningOpen(false);
            setRebookConfirmed(!rebookConfirmed);
        }
    };

    useEffect(() => {
        props.reloadDoorStatus();
    }, [rebookConfirmed]);

    const showRebookDoorWarning = (door: DoorMapping, index: number) => {
        setSelectedDoorToRebook(door);
        setDoorIndex(index);
        setRebookDoorWarningOpen(true);
    };
    
    const getBooking = (orderId: string | undefined) => {
        return bookingsList?.find((booking) => booking?.id === orderId);
    };

    const getBookingForDoor = (door: FullMappedDoor, index: number): Booking | undefined => {
        return (tenant === 'segmenta' || tenant === 'vonovia')
            ? (bookings[index] || undefined)
            : getBooking(door?.orderId);
    };

    const matchesSearch = (door: FullMappedDoor, index: number): boolean => {
        if (!props.searchQuery) return true;
        const query = props.searchQuery.toLowerCase();
        // Locker number: exact match (e.g. "6" finds only locker 6, not 16)
        if (door.door.toString() === query) return true;
        // Locker size: exact match (e.g. "S" finds only S, not XS)
        if (door.assetName?.toLowerCase() === query) return true;
        // Email & booking number: only for queries with 3+ characters
        // (short queries like "s" or "6" would match too many irrelevant results)
        if (query.length >= 3) {
            if (door.bookingNumber?.toLowerCase().includes(query)) return true;
            const booking = getBookingForDoor(door, index);
            if (booking?.contactData?.email?.toLowerCase().includes(query)) return true;
        }
        return false;
    };

    const filteredDoorMapping = props.doorMapping
        .map((door, originalIndex) => ({ door, originalIndex }))
        .filter(({ door, originalIndex }) => matchesSearch(door, originalIndex));

    return (
        <>
            <div className={'flex flex-col gap-2'}>
                {filteredDoorMapping.map(({ door, originalIndex }) => {
                    const booking = getBookingForDoor(door, originalIndex);
                    const hasBookingAndNotBlocked = booking && door.assetState !== 'blocked';
                    return (
                        <DoorListItem
                            key={door.door}
                            doorNumber={door.door}
                            doorStatus={door}
                            depotId={props.depot.id}
                            locationId={props.depot.locationId}
                            enableLockDoor={true}
                            enableUnLockDoor={true}
                            enableRebookDoor={true}
                            onOpenClick={() => props.onOpenClick(door.door)}
                            onLockClick={() => props.onLockOrUnlockClick('block', door.door)}
                            onUnlockClick={() => props.onLockOrUnlockClick('unblock', door.door)}
                            onRebookDoorClick={hasBookingAndNotBlocked
                                ? () => showRebookDoorWarning(door, originalIndex)
                                : undefined
                            }
                            booking={booking}
                        />
                    );
                })}
            </div>
            {
                selectedDoorToRebook && doorIndex !== null && (
                    <RebookCustomerDoorDialog
                        open={rebookDoorWarningOpen}
                        doorNumber={Number(selectedDoorToRebook.door)}
                        depotName={props.depot.name}
                        locationName={props.depot.name}
                        onConfirm={() => handleRebookDoor(selectedDoorToRebook)}
                        onCancel={() => setRebookDoorWarningOpen(false)}
                    />
                )
            }
        </>
    );
};

export default DoorList;
